<script setup lang="ts">
import { computed } from 'vue'

import { TabContainer, Tab } from '../shared/tabs'
import ParagraphTypeSlider from './ParagraphTypeSlider.vue'
import ParagraphTypeEmbed from './ParagraphTypeEmbed.vue'
import ParagraphTypeImage from './ParagraphTypeImage.vue'
import ParagraphTypeTmdbImage from './ParagraphTypeTmdbImage.vue'

import type { Brand } from '@xcel-next/core/enums'
import type { EmbedParagraph, SliderGalleryItem, VodEpgBroadcastSliderTrailerItem, VodEpgBroadcastSliderImageItem } from '../../utils/paragraph-schema/paragraphs'

const props = withDefaults(defineProps<{
  trailerItems?: VodEpgBroadcastSliderTrailerItem[],
  imageItems?: VodEpgBroadcastSliderImageItem[],
  brand?: Brand,
  isPreview?: boolean,
  listOrderPosition?: number
}>(), {
  trailerItems: () => [],
  imageItems: () => [],
  isPreview: false,
  listOrderPosition: 0
})

type ResolvedTrailerItem = { paragraphType: NonNullable<VodEpgBroadcastSliderTrailerItem['paragraphType']>, embed: EmbedParagraph['embed'] }
type ResolvedImageItem = { paragraphType: NonNullable<VodEpgBroadcastSliderImageItem['paragraphType']>, image: VodEpgBroadcastSliderImageItem['image'], tmdbImage: VodEpgBroadcastSliderImageItem['tmdbImage'] }

// composer only guarantees `raw`/`paragraphType` are set once actually populated, filtering narrows out the theoretical gaps in the schema's optionals
const resolvedTrailerItems = computed(() =>
  props.trailerItems.filter((item): item is ResolvedTrailerItem => !!item.paragraphType && !!item.embed?.raw)
)

const transformedTrailerItems = computed((): SliderGalleryItem[] =>
  resolvedTrailerItems.value.map(({ paragraphType, embed }) => ({ paragraphType, embed }))
)

const transformedImageItems = computed((): SliderGalleryItem[] =>
  props.imageItems
    .filter((item): item is ResolvedImageItem => !!item.paragraphType)
    .map(({ paragraphType, image, tmdbImage }) => ({ paragraphType, image, tmdbImage }))
)

const singleTrailerEmbed = computed(() => resolvedTrailerItems.value[0]?.embed)

const shouldRenderComponent = computed(() => props.imageItems.length !== 0 || props.trailerItems.length !== 0)
const shouldRenderTrailers = computed(() => props.trailerItems.length !== 0)
const shouldRenderImages = computed(() => props.imageItems.length !== 0)
const shouldRenderSingleTrailer = computed(() => props.trailerItems.length === 1)
const shouldRenderSingleImage = computed(() => props.imageItems.length === 1)

const getLoadingValue = (index: number): 'eager' | 'lazy' => index === 0 ? 'eager' : 'lazy'
</script>

<template>
  <div v-if="shouldRenderComponent"
       class="bx-content bx-vod-epg-broadcast-slider">
    <TabContainer :header-border-visible="true">
      <Tab v-if="shouldRenderTrailers"
           title="TRAILER">
        <template v-if="shouldRenderSingleTrailer">
          <ParagraphTypeEmbed :embed="singleTrailerEmbed"
                              :is-preview="isPreview" />
        </template>
        <template v-else>
          <ParagraphTypeSlider :brand="brand"
                               :gallery-items="transformedTrailerItems"
                               :single-slide-urls="false"
                               :is-preview="isPreview" />
        </template>
      </Tab>
      <Tab v-if="shouldRenderImages"
           title="BILDER">
        <template v-if="shouldRenderSingleImage">
          <ParagraphTypeImage v-if="imageItems[0].paragraphType === 'image'"
                              :image="imageItems[0].image!"
                              :brand="brand"
                              :list-order-position="listOrderPosition"
                              :loading="getLoadingValue(0)" />
          <ParagraphTypeTmdbImage v-else-if="imageItems[0].paragraphType === 'tmdbImage'"
                                  :image="imageItems[0].tmdbImage!"
                                  :loading="getLoadingValue(0)" />
        </template>
        <template v-else>
          <ParagraphTypeSlider :brand="brand"
                               :gallery-items="transformedImageItems"
                               :single-slide-urls="false"
                               :is-preview="isPreview" />
        </template>
      </Tab>
    </TabContainer>
  </div>
</template>
